import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import MainContent from '../components/MainContent';
import ReminderSettings from '../components/reminders/ReminderSettings';
import ReminderPreview from '../components/reminders/ReminderPreview';
import ReminderLog from '../components/reminders/ReminderLog';
import { CARD } from '../components/reminders/reminderText';
import {
  reminderError,
  useReminderSettings,
} from '../hooks/useCohortReminders';
import { canManageCohortAlerts } from '../utils/permissionKeys';

// Automatic reminders to a cohort's learners who haven't submitted an
// assignment: settings, what the next 72 hours would send, and the log.
export default function CohortReminders() {
  const { cohortStatus, cohortName, cohortId } = useParams();
  const permissions = useSelector((state) => state.auth.permissions ?? []);
  const role = useSelector((state) => state.auth.userDetails?.role);
  const { data, isLoading, error } = useReminderSettings(cohortId);
  const cohortPath = `/cohorts=${cohortStatus}/${cohortName}/${cohortId}`;

  if (!canManageCohortAlerts(permissions, role))
    return <Navigate to={cohortPath} replace />;

  return (
    <MainContent full>
      <div className="space-y-5 pb-10">
        <header className="space-y-1">
          <Link
            to={cohortPath}
            className="text-xs text-claret-500 hover:underline"
          >
            ← {cohortName}
          </Link>
          <h1 className="text-2xl font-semibold text-gray-900">
            Assignment reminders
          </h1>
          <p className="text-xs text-gray-500">
            Reminds {cohortName} learners who haven&apos;t submitted, before
            each deadline (in-app and email, SMS on the final one), and tells
            them if they missed it.
          </p>
        </header>

        {isLoading && (
          <div className={`${CARD} h-64 animate-pulse`} aria-label="Loading" />
        )}
        {error && (
          <div className={`${CARD} p-8 text-center text-sm text-gray-600`}>
            {reminderError(error, "We couldn't load the reminder settings.")}
          </div>
        )}
        {data && (
          <>
            {data.server_enabled === false && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                Reminders are switched off on the server. Settings are saved,
                but nothing will be sent until they&apos;re switched on there.
              </div>
            )}
            <ReminderSettings cohortId={cohortId} data={data} />
            <ReminderPreview cohortId={cohortId} />
            <ReminderLog
              cohortId={cohortId}
              hours={data.settings?.reminder_hours ?? []}
            />
          </>
        )}
      </div>
    </MainContent>
  );
}
